import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Users, FileText, DollarSign, TrendingUp, Shield } from 'lucide-react';
import api from '../utils/api';
import { toast } from 'sonner';

const AdminPanel = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    if (user.role !== 'admin') {
      toast.error('Acceso denegado');
      navigate('/dashboard');
      return;
    }
    fetchData();
  }, [user]);

  const fetchData = async () => {
    try {
      const [statsData, usersData] = await Promise.all([
        api.getAdminStats(),
        api.getAllUsers()
      ]);
      setStats(statsData);
      setUsers(usersData);
    } catch (error) {
      toast.error('Error al cargar datos de administración');
    } finally {
      setLoading(false);
    }
  };

  if (!user || user.role !== 'admin') return null;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#F5F5F0' }}>
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 mx-auto" style={{ borderColor: '#1A4D2E' }}></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#F5F5F0' }}>
      <nav className="bg-white border-b border-border shadow-sm">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl">
          <div className="flex items-center justify-between h-16">
            <Link to="/dashboard" className="flex items-center gap-3">
              <img src="/assets/logo.png" alt="contractcheck360" className="h-10 w-auto" />
              <span className="text-xl brand-name" style={{ color: '#1A4D2E' }}>
                contractcheck360
              </span>
            </Link>
            <div className="flex items-center gap-4">
              <Shield className="w-5 h-5" style={{ color: '#1A4D2E' }} />
              <span className="text-sm font-medium">Panel Admin</span>
              <Link to="/dashboard">
                <Button variant="outline">Dashboard</Button>
              </Link>
            </div>
          </div>
        </div>
      </nav>

      <div className="container mx-auto px-4 md:px-8 max-w-7xl py-8">
        <h1 className="text-3xl md:text-4xl font-bold mb-8" style={{ fontFamily: 'Merriweather, serif', color: '#1A4D2E' }}>
          Panel de Administración
        </h1>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white border border-border shadow-sm rounded-sm p-6">
            <div className="flex items-center justify-between mb-2">
              <Users className="w-8 h-8" style={{ color: '#1A4D2E' }} />
            </div>
            <p className="text-3xl font-bold" style={{ color: '#1A4D2E' }}>{stats?.total_users || 0}</p>
            <p className="text-sm" style={{ color: '#64748B' }}>Usuarios Totales</p>
          </div>

          <div className="bg-white border border-border shadow-sm rounded-sm p-6">
            <div className="flex items-center justify-between mb-2">
              <FileText className="w-8 h-8" style={{ color: '#1A4D2E' }} />
            </div>
            <p className="text-3xl font-bold" style={{ color: '#1A4D2E' }}>{stats?.total_contracts || 0}</p>
            <p className="text-sm" style={{ color: '#64748B' }}>Contratos Subidos</p>
          </div>

          <div className="bg-white border border-border shadow-sm rounded-sm p-6">
            <div className="flex items-center justify-between mb-2">
              <TrendingUp className="w-8 h-8" style={{ color: '#1A4D2E' }} />
            </div>
            <p className="text-3xl font-bold" style={{ color: '#1A4D2E' }}>{stats?.total_analyses || 0}</p>
            <p className="text-sm" style={{ color: '#64748B' }}>Análisis Realizados</p>
          </div>

          <div className="bg-white border border-border shadow-sm rounded-sm p-6">
            <div className="flex items-center justify-between mb-2">
              <DollarSign className="w-8 h-8" style={{ color: '#F97316' }} />
            </div>
            <p className="text-3xl font-bold" style={{ color: '#F97316' }}>${stats?.total_revenue?.toFixed(2) || '0.00'}</p>
            <p className="text-sm" style={{ color: '#64748B' }}>Ingresos Totales (MXN)</p>
          </div>
        </div>

        {/* Monthly Stats */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          <div className="bg-white border border-border shadow-sm rounded-sm p-6">
            <h3 className="text-xl font-semibold mb-4" style={{ fontFamily: 'Merriweather, serif' }}>Este Mes</h3>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span style={{ color: '#64748B' }}>Contratos subidos:</span>
                <span className="font-semibold">{stats?.contracts_this_month || 0}</span>
              </div>
              <div className="flex justify-between">
                <span style={{ color: '#64748B' }}>Análisis realizados:</span>
                <span className="font-semibold">{stats?.analyses_this_month || 0}</span>
              </div>
            </div>
          </div>

          <div className="bg-white border border-border shadow-sm rounded-sm p-6">
            <h3 className="text-xl font-semibold mb-4" style={{ fontFamily: 'Merriweather, serif' }}>Precios Actuales</h3>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span style={{ color: '#64748B' }}>Análisis Individual:</span>
                <span className="font-semibold">$59.90 MXN</span>
              </div>
              <div className="flex justify-between">
                <span style={{ color: '#64748B' }}>Comparación (2):</span>
                <span className="font-semibold">$99.00 MXN</span>
              </div>
              <div className="flex justify-between">
                <span style={{ color: '#64748B' }}>Comparación (3):</span>
                <span className="font-semibold">$129.00 MXN</span>
              </div>
            </div>
          </div>
        </div>

        {/* Users Table */}
        <div className="bg-white border border-border shadow-sm rounded-sm overflow-hidden">
          <div className="p-6 border-b">
            <h3 className="text-xl font-semibold" style={{ fontFamily: 'Merriweather, serif' }}>Usuarios Registrados</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="border-b" style={{ backgroundColor: '#F5F5F0' }}>
                <tr>
                  <th className="text-left p-4 font-semibold">Usuario</th>
                  <th className="text-left p-4 font-semibold">Email</th>
                  <th className="text-left p-4 font-semibold">Rol</th>
                  <th className="text-left p-4 font-semibold">Contratos</th>
                  <th className="text-left p-4 font-semibold">Pagos Total</th>
                  <th className="text-left p-4 font-semibold">Registro</th>
                  <th className="text-left p-4 font-semibold">Estado</th>
                </tr>
              </thead>
              <tbody>
                {users.map((u, index) => (
                  <tr key={u.id} className={`border-b ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}>
                    <td className="p-4 font-medium">{u.full_name}</td>
                    <td className="p-4">{u.email}</td>
                    <td className="p-4">
                      <span className={`px-2 py-1 rounded text-xs font-medium ${
                        u.role === 'admin' ? 'bg-purple-100 text-purple-800' : 'bg-blue-100 text-blue-800'
                      }`}>
                        {u.role}
                      </span>
                    </td>
                    <td className="p-4">{u.total_contracts}</td>
                    <td className="p-4">${u.total_payments?.toFixed(2) || '0.00'}</td>
                    <td className="p-4 text-sm" style={{ color: '#64748B' }}>
                      {new Date(u.created_at).toLocaleDateString('es-ES')}
                    </td>
                    <td className="p-4">
                      <span className={`px-2 py-1 rounded text-xs font-medium ${
                        u.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                      }`}>
                        {u.is_active ? 'Activo' : 'Inactivo'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminPanel;